class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.detailsModal = this.closest("details-modal-search");
    this.isOnInitialResult = true;

    this.setupEventListeners();
    this.updateSearchHistory();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
    this.detailsModal
      .querySelector(".search-modal__overlay")
      ?.addEventListener("click", () => {
        this.resetForm();
      });
  }

  getInitialResults() {
    if (this.isOnInitialResult) {
      return;
    } else {
      this.isOnInitialResult = true;
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults["$empty"]) {
      this.renderSearchResults(this.cachedResults["$empty"]);
      return;
    }

    fetch(window.Shopify.routes.root + "?sections=predictive-search")
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.json();
      })
      .then(({ "predictive-search": text }) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults["$empty"] = resultsMarkup;
          }
        );

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    // Bloquear preview para "Sale" ou "Sales" (case-insensitive)
    const blockedTerms = ['sale', 'sales'];
    if (blockedTerms.includes(newSearchTerm.toLowerCase())) {
      this.getCustomResults(newSearchTerm);
      return;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (this.searchTerm == "") {
      this.getInitialResults();
      return;
    }

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.resetForm();
    }
  }

  resetForm() {
    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
    this.detailsModal.close();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        removeTrapFocus(this.activeElement);
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.key) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }

    if (event.key === "Enter") {
      const target = event.target;
      if (target.type === "search" && target.form != null) {
        event.preventDefault();
        target.form.submit();
      }
    }
  }

  updateSearchHistory() {
    try {
      const filteredSearchTermsFromLocalStorage = JSON.parse(
        localStorage.getItem("predictive-search-terms") || "[]"
      );

      const filteredSearchTermsFromCachedResults = Object.keys(
        this.cachedResults
      )
        .filter((item) => item != "$empty")
        .filter((item) => item.length > 4)
        .reverse()
        .slice(0, 3);

      const filteredSearchTerms = Array.from(
        new Set([
          ...filteredSearchTermsFromCachedResults,
          ...filteredSearchTermsFromLocalStorage,
        ])
      ).slice(0, 3);

      const historyTemplate = this.querySelector(
        "#predictive-search-history-list"
      );

      const suggestionsList = this.querySelector(
        "#predictive-search-results-suggestions-list"
      );

      [...filteredSearchTerms].reverse().forEach((term) => {
        historyTemplate.content.querySelector("p").textContent = term;
        historyTemplate.content.querySelector("a").href = `/search?q=${term}`;
        suggestionsList.prepend(
          document.importNode(historyTemplate.content, true)
        );
      });

      localStorage.setItem(
        "predictive-search-terms",
        JSON.stringify(filteredSearchTerms)
      );
    } catch (error) {
      console.error(error);
    }
  }

  updateSearchHistory() {
    const historyTemplate = this.querySelector(
      "#predictive-search-history-list"
    );

    const suggestionsList = this.querySelector(
      "#predictive-search-results-suggestions-list"
    );

    const newestSearchHistory = this.getNewestSearchHistory();

    this.saveSearchHistory(newestSearchHistory);

    if (!historyTemplate) {
      return;
    }

    newestSearchHistory.reverse().forEach((term) => {
      historyTemplate.content.querySelector("p").textContent = term;
      historyTemplate.content.querySelector("a").href = `/search?q=${term}`;

      suggestionsList.prepend(
        document.importNode(historyTemplate.content, true)
      );
    });
  }

  saveSearchHistory(filteredSearchTerms) {
    localStorage.setItem(
      "predictive-search-terms",
      JSON.stringify(filteredSearchTerms)
    );
  }

  getNewestSearchHistory() {
    const filteredSearchTermsFromLocalStorage = JSON.parse(
      localStorage.getItem("predictive-search-terms") || "[]"
    );

    const filteredSearchTermsFromCachedResults = Object.keys(this.cachedResults)
      .filter((item) => item != "$empty")
      .filter((item) => item.length > 4)
      .reverse()
      .slice(0, 3);

    const filteredSearchTerms = Array.from(
      new Set([
        ...filteredSearchTermsFromCachedResults,
        ...filteredSearchTermsFromLocalStorage,
      ])
    ).slice(0, 3);

    return filteredSearchTerms;
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g"))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();
    this.isOnInitialResult = false;

    if (this.cachedResults[queryKey]) {
      const resultsMarkup = this.cachedResults[queryKey];

      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        delete predictiveSearchInstance.cachedResults[queryKey];
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });

      const newestSearchHistory = this.getNewestSearchHistory();
      this.saveSearchHistory(newestSearchHistory);
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor&resources[limit]=6`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  getCustomResults(searchTerm) {
    this.setLiveRegionLoadingState();
    this.isOnInitialResult = false;
    this.searchTerm = searchTerm;

    // Usa um termo impossível para garantir 0 resultados
    const impossibleSearchTerm = "xyzabc123impossibletermnotfound987";

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        impossibleSearchTerm
      )}&section_id=predictive-search&resources[type]=product&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor&resources[limit]=6`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        let resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        
        // Substitui o termo impossível pelo termo real digitado pelo usuário
        resultsMarkup = resultsMarkup.replace(
          new RegExp(impossibleSearchTerm, 'g'),
          searchTerm
        );
        
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const swiperInResults = document.querySelector(
      "#predictive-results-swiper-script"
    );

    if (swiperInResults) {
      nodeScriptReplace(swiperInResults);
    }

    this.setAttribute("results", true);
    this.dispatchWishListEvent();

    this.setLiveRegionResults();
    this.open();

    trapFocus(
      this.closest("details-modal-search").querySelector('[tabindex="-1"]'),
      this.closest("details-modal-search").querySelector(
        'input:not([type="hidden"])'
      )
    );

    this.updateSearchHistory();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  dispatchWishListEvent() {
    document.dispatchEvent(
      new CustomEvent("swym:predictive-search-results-loaded")
    );
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    // this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    // this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);

function nodeScriptReplace(node) {
  if (nodeScriptIs(node) === true) {
    node.parentNode.replaceChild(nodeScriptClone(node), node);
  } else {
    var i = -1,
      children = node.childNodes;
    while (++i < children.length) {
      nodeScriptReplace(children[i]);
    }
  }

  return node;
}

function nodeScriptClone(node) {
  var script = document.createElement("script");
  script.text = node.innerHTML;

  var i = -1,
    attrs = node.attributes,
    attr;
  while (++i < attrs.length) {
    script.setAttribute((attr = attrs[i]).name, attr.value);
  }
  return script;
}

function nodeScriptIs(node) {
  return node.tagName === "SCRIPT";
}